import { useState } from 'react';
import { Link } from 'react-router';
import { disconnectLidl, finishLidlConnect, requestLidlSync, setCouponsMode, startLidlConnect, useLidlStatus, type CouponsMode, type LidlStatus } from '../data/lidl';
import { agoLabel } from '../data/prices';
import { cardCls, inputCls, primaryBtn, secondaryBtn, sectionTitleCls } from './ui/classes';

/** Zakładka Dom: połączenie z Lidl Plus — paragony pobierają się same, a ceny trafiają do list. */
export function LidlSection() {
  const { status, refresh } = useLidlStatus();
  return (
    <section id="lidl" className="flex flex-col gap-2">
      <h2 className={sectionTitleCls}>Paragony z Lidla</h2>
      {status === undefined ? (
        <div className={`${cardCls} h-24 animate-pulse`} />
      ) : status === null ? (
        <div className={`${cardCls} p-4 text-sm text-stone-600 dark:text-stone-400`}>Do połączenia z Lidl Plus potrzebny jest internet.</div>
      ) : status.status === 'connected' ? (
        <Connected status={status} onChange={refresh} />
      ) : (
        <Connect expired={status.status === 'expired'} onConnected={refresh} />
      )}
    </section>
  );
}

function Connected({ status, onChange }: { status: LidlStatus; onChange: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const sync = async () => {
    setBusy(true);
    await requestLidlSync('manual');
    await onChange();
    setBusy(false);
  };
  const disconnect = async () => {
    if (!confirm('Odłączyć Lidl Plus?\n\nPobrane paragony i ceny zostaną. Nowe paragony przestaną przychodzić.')) return;
    setBusy(true);
    await disconnectLidl().catch(() => {});
    await onChange();
    setBusy(false);
  };
  const p = status.progress;
  return (
    <div className={`${cardCls} flex flex-col gap-3 p-4`}>
      <div className="flex items-center gap-3">
        <span aria-hidden className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-lg dark:bg-brand-800/30">
          🧾
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium">Lidl Plus połączony</p>
          <p className="text-sm text-stone-500">
            <Link to="/prices" className="text-brand-700 underline underline-offset-2 dark:text-brand-500">
              {status.receipts} {status.receipts === 1 ? 'paragon' : 'paragonów'}
            </Link>
            {status.lastOkAt ? ` · pobrano ${agoLabel(status.lastOkAt)}` : ''}
          </p>
        </div>
      </div>
      {status.running && (
        <p className="rounded-xl bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
          {p && p.total > 0 ? `Pobieram paragony: ${p.done} z ${p.total}…` : 'Sprawdzam nowe paragony…'}
          {p && p.total > 20 && <span className="block text-xs opacity-80">Pierwsze pobranie całej historii trwa kilkanaście minut — możesz zamknąć aplikację.</span>}
        </p>
      )}
      {status.lastError && !status.running && <p className="text-sm text-amber-700 dark:text-amber-300">Ostatnia próba: {status.lastError}</p>}
      <p className="text-sm text-stone-600 dark:text-stone-400">Nowe paragony przychodzą same co godzinę w ciągu dnia i kilka minut po „Zakończ zakupy” w Lidlu.</p>
      <Coupons status={status} onChange={onChange} />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => void sync()} disabled={busy || status.running} className={secondaryBtn}>
          Pobierz teraz
        </button>
        <button type="button" onClick={() => void disconnect()} disabled={busy} className={`${secondaryBtn} text-red-600 dark:text-red-400`}>
          Odłącz
        </button>
      </div>
    </div>
  );
}

const MODES: { mode: CouponsMode; label: string }[] = [
  { mode: 'off', label: 'Wyłączone' },
  { mode: 'dry', label: 'Próbnie' },
  { mode: 'on', label: 'Aktywuj' },
];
const DRY_DAYS = 7;
const STATUS_TEXT = { would: 'aktywowałbym', activated: 'aktywowany', failed: 'nie udało się' } as const;

/** Kupony Lidl Plus: Odhaczka aktywuje je na produkty z list i kupowane regularnie (najpierw tydzień próbny). */
function Coupons({ status, onChange }: { status: LidlStatus; onChange: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const pick = async (mode: CouponsMode) => {
    if (mode === status.couponsMode) return;
    setBusy(true);
    await setCouponsMode(mode).catch(() => {});
    await onChange();
    setBusy(false);
  };
  const until = status.couponsModeAt ? new Date(status.couponsModeAt + DRY_DAYS * 86_400_000).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' }) : null;
  const text =
    status.couponsMode === 'dry'
      ? `Tryb próbny${until ? ` do ${until}` : ''}: zapisuję, które kupony bym aktywował. Potem aktywacja włączy się sama.`
      : status.couponsMode === 'on'
        ? 'Aktywuję kupony na produkty z aktywnych list i kupowane regularnie.'
        : 'Kupony aktywujesz sam w aplikacji Lidl Plus.';
  return (
    <div className="flex flex-col gap-2 border-t border-stone-200 pt-3 dark:border-stone-800">
      <p className="font-medium">Kupony Lidl Plus</p>
      <div role="radiogroup" aria-label="Kupony Lidl Plus" className="grid grid-cols-3 gap-1 rounded-xl bg-stone-100 p-1 dark:bg-stone-800">
        {MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            role="radio"
            aria-checked={status.couponsMode === m.mode}
            disabled={busy}
            onClick={() => void pick(m.mode)}
            className={`min-h-10 rounded-lg text-sm font-medium ${
              status.couponsMode === m.mode ? 'bg-white shadow-sm dark:bg-stone-900' : 'text-stone-600 dark:text-stone-400'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="text-sm text-stone-600 dark:text-stone-400">{text}</p>
      {status.couponLog.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm">
          {status.couponLog.slice(0, 6).map((l, i) => (
            <li key={i} className="flex gap-2">
              <span className={`shrink-0 ${l.status === 'activated' ? 'text-brand-700 dark:text-brand-500' : l.status === 'failed' ? 'text-red-600' : 'text-stone-500'}`}>
                {STATUS_TEXT[l.status]}
              </span>
              <span className="min-w-0 truncate">
                {l.title}
                {l.discount ? <span className="text-stone-500"> · {l.discount}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Connect({ expired, onConnected }: { expired: boolean; onConnected: () => Promise<void> }) {
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prepare = async () => {
    setError(null);
    setCopied(false);
    try {
      const url = await startLidlConnect();
      setAuthUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      } catch {
        // Bez schowka adres i tak jest widoczny do skopiowania ręcznie.
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const finish = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await finishLidlConnect(value);
      setValue('');
      setAuthUrl(null);
      await onConnected();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${cardCls} flex flex-col gap-3 p-4`}>
      {expired ? (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          Połączenie z Lidl Plus wygasło. Zaloguj się ponownie, żeby paragony dalej przychodziły.
        </p>
      ) : (
        <p className="text-sm text-stone-600 dark:text-stone-400">
          Połącz konto Lidl Plus, a Odhaczka sama pobierze e-paragony, dopasuje je do list i pokaże ceny przy pozycjach. Wystarczy jedno konto na cały dom.
        </p>
      )}
      <p className="text-sm text-stone-600 dark:text-stone-400">
        Zrób to raz, na komputerze w Chrome albo Edge (odhaczka.pl w przeglądarce). Na telefonie logowanie przejęłaby aplikacja Lidl Plus.
      </p>
      <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-stone-700 dark:text-stone-300">
        <li>
          <button type="button" onClick={() => void prepare()} className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-500">
            {authUrl ? 'Skopiuj adres logowania jeszcze raz' : 'Skopiuj adres logowania Lidla'}
          </button>
          {copied && <span className="ml-1 text-brand-700 dark:text-brand-500">✓ skopiowany</span>}
          {authUrl && !copied && (
            <textarea readOnly value={authUrl} rows={3} className={`${inputCls} mt-1 font-mono text-xs`} onFocus={(e) => e.currentTarget.select()} />
          )}
        </li>
        <li>
          Otwórz nową kartę i wejdź na <b>lidl.pl</b> (nie zostawiaj pustej „Nowej karty”). Naciśnij <b>F12</b>, wejdź w zakładkę <b>Network</b> (Sieć), zaznacz{' '}
          <b>Preserve log</b> (Zachowaj dziennik), wybierz filtr <b>All</b> (Wszystkie), a w polu filtra wpisz <code>callback?code</code>.
        </li>
        <li>
          Wklej adres logowania w pasek adresu tej karty i naciśnij Enter. Zaloguj się, jeśli Lidl o to poprosi. Strona potem się nie zmieni albo zostanie pusta — tak
          ma być.
        </li>
        <li>
          W Network pojawi się wiersz <b>callback?code=…</b> (status <i>canceled</i> albo <i>failed</i>). Kliknij go prawym przyciskiem → <b>Copy</b> → <b>Copy URL</b>{' '}
          (Kopiuj adres URL) i wklej poniżej w ciągu kilku minut. Adres zaczyna się od <code className="break-all">com.lidlplus.app://callback?code=</code>.
        </li>
      </ol>
      <form onSubmit={finish} className="flex flex-col gap-2">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={3}
          placeholder="com.lidlplus.app://callback?code=…"
          className={`${inputCls} font-mono text-sm`}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <button disabled={busy || !value.trim()} className={primaryBtn}>
          {busy ? 'Łączę…' : 'Połącz'}
        </button>
      </form>
    </div>
  );
}
